import { z } from 'zod';

import {
    FETCH_YOURSELF,
    PROMOZAVR_MOBILE_CHECKER_COLLECTION_ID,
    PROMOZAVR_PERSISTENT_PROJECT,
    PROMOZAVR_PERSISTENT_SKIP_CHECKERS,
    PROMOZAVR_PERSISTENT_TOUCH_PROJECT,
    PROMOZAVR_PROJECT,
    PROMOZAVR_STAGE,
    PROMOZAVR_TEST_APP,
    PROMOZAVR_TOUCH_PROJECT,
    type SelectMultiplePromosModelInput,
    type SelectMultiplePromosModelResponse,
    type SelectMultiplePromosModelServerInput,
    type SelectPromoModelInput,
    type SelectPromoModelResponse,
    type SelectPromoModelServerInput,
} from './types/promozavr';

const promozavrFormatSchema = z.string();

const promoSchema = z
    .object({
        name: z.string(),
        stage: z.string(),
    })
    .passthrough();

const selectorMetadataSchema = z
    .object({
        queueName: z.string().optional(),
    })
    .passthrough();

const promoWithFormatSchema = z
    .object({
        promo: promoSchema,
        format: promozavrFormatSchema,
        isPersistent: z.boolean().optional(),
        selectorMetadata: selectorMetadataSchema.optional(),
    })
    .passthrough();

const translationLeafSchema = z
    .object({
        form: z.string(),
    })
    .passthrough();

const translationKeySchema = z
    .object({
        translations: z.record(z.string(), translationLeafSchema),
    })
    .passthrough();

const translationKeysetSchema = z
    .object({
        keys: z.record(z.string(), translationKeySchema),
    })
    .passthrough();

const translationsSchema = z.object({
    keysets: z.record(z.string(), translationKeysetSchema),
});

const promoImageBaseSchema = z
    .object({
        name: z.string(),
        theme: z.string(),
    })
    .passthrough();

const imageUrlsSchema = z.record(z.string(), z.string());

const promoImageSchema = promoImageBaseSchema.extend({
    urls: imageUrlsSchema,
});

const promoCssBackgroundSchema = promoImageBaseSchema.extend({
    css: z.string(),
});

const promoIconSchema = promoImageBaseSchema.extend({
    url: z.string(),
});

const promoAnimationSchema = promoImageBaseSchema.extend({
    type: z.string(),
    url: z.string(),
    autoplay: z.boolean().optional(),
    loop: z.boolean().optional(),
});

const imageStoreSchema = z.object({
    pictures: z.array(promoImageSchema).default([]),
    backgrounds: z.array(promoImageSchema).default([]),
    backgroundsCss: z.array(promoCssBackgroundSchema).default([]),
    icons: z.array(promoIconSchema).default([]),
    animations: z.array(promoAnimationSchema).default([]),
});

const selectPromoSuppliersDataSchema = z
    .object({
        bunkerConfigSupplierData: z.literal(FETCH_YOURSELF),
        billingDataSupplierData: z.literal(FETCH_YOURSELF),
        billingSubscriptionsDataSupplierData: z.literal(FETCH_YOURSELF),
        blackboxUserInfoSupplierData: z.literal(FETCH_YOURSELF),
        datasyncPromoDataSupplierData: z.literal(FETCH_YOURSELF),
        datasyncUserFeaturesDataSupplierData: z.literal(FETCH_YOURSELF),
        saasKvSegmentsSupplierData: z.literal(FETCH_YOURSELF),
    })
    .strict();

const promozavrAppSchema = z.literal(PROMOZAVR_TEST_APP);
const promozavrBunkerVersionSchema = z.enum(['test', 'prod']);
const promozavrPersistentSkipCheckerSchema = z.literal(PROMOZAVR_PERSISTENT_SKIP_CHECKERS[0]);
const promozavrMobileCheckerCollectionSchema = z.literal(PROMOZAVR_MOBILE_CHECKER_COLLECTION_ID);

const selectPromoModelEntryBaseSchema = z
    .object({
        stage: z.literal(PROMOZAVR_STAGE),
        lang: z.string().optional(),
        tld: z.string().optional(),
        testIds: z.array(z.string()),
        checkerCollectionId: z.never().optional(),
        debugParams: z.string().optional(),
        skipPromos: z.array(z.string()),
        selectAllPromos: z.literal(false),
    })
    .strict();

const selectPromoModelRegularEntrySchema = selectPromoModelEntryBaseSchema
    .extend({
        project: z.literal(PROMOZAVR_PROJECT),
        usePersistentQueues: z.literal(false),
    })
    .strict();

const selectPromoModelTouchRegularEntrySchema = selectPromoModelEntryBaseSchema
    .extend({
        project: z.literal(PROMOZAVR_TOUCH_PROJECT),
        usePersistentQueues: z.literal(false),
        checkerCollectionId: promozavrMobileCheckerCollectionSchema,
    })
    .strict();

const selectPromoModelPersistentEntrySchema = selectPromoModelEntryBaseSchema
    .extend({
        project: z.literal(PROMOZAVR_PERSISTENT_PROJECT),
        usePersistentQueues: z.literal(true),
        skipCheckers: z.array(promozavrPersistentSkipCheckerSchema),
    })
    .strict();

const selectPromoModelTouchPersistentEntrySchema = selectPromoModelEntryBaseSchema
    .extend({
        project: z.literal(PROMOZAVR_PERSISTENT_TOUCH_PROJECT),
        usePersistentQueues: z.literal(true),
        checkerCollectionId: promozavrMobileCheckerCollectionSchema,
        skipCheckers: z.array(promozavrPersistentSkipCheckerSchema),
    })
    .strict();

const selectPromoModelInputBaseSchema = selectPromoModelRegularEntrySchema
    .extend({
        app: promozavrAppSchema.optional(),
        debug: z.string().optional(),
        suppliersData: selectPromoSuppliersDataSchema,
    })
    .strict();

export const selectPromoModelInputSchema: z.ZodType<SelectPromoModelInput> = selectPromoModelInputBaseSchema;

export const selectPromoModelServerInputSchema: z.ZodType<SelectPromoModelServerInput> = selectPromoModelInputBaseSchema
    .extend({
        bunkerVersion: promozavrBunkerVersionSchema.optional(),
        fastFail: z.boolean().optional(),
    })
    .strict();

export const transportSelectPromoModelInputSchema = selectPromoModelInputSchema;

const selectMultiplePromosModelDesktopEntriesSchema = z
    .object({
        regular: selectPromoModelRegularEntrySchema,
        persistent: selectPromoModelPersistentEntrySchema,
    })
    .strict();

const selectMultiplePromosModelTouchEntriesSchema = z
    .object({
        regular: selectPromoModelTouchRegularEntrySchema,
        persistent: selectPromoModelTouchPersistentEntrySchema,
    })
    .strict();

const selectMultiplePromosModelInputBaseSchema = z
    .object({
        app: promozavrAppSchema.optional(),
        debug: z.string().optional(),
        entries: z.union([selectMultiplePromosModelDesktopEntriesSchema, selectMultiplePromosModelTouchEntriesSchema]),
        suppliersData: selectPromoSuppliersDataSchema,
    })
    .strict();

export const selectMultiplePromosModelInputSchema: z.ZodType<SelectMultiplePromosModelInput> =
    selectMultiplePromosModelInputBaseSchema;

export const selectMultiplePromosModelServerInputSchema: z.ZodType<SelectMultiplePromosModelServerInput> =
    selectMultiplePromosModelInputBaseSchema
        .extend({
            bunkerVersion: promozavrBunkerVersionSchema.optional(),
            fastFail: z.boolean().optional(),
        })
        .strict();

export const transportSelectMultiplePromosModelInputSchema = selectMultiplePromosModelInputSchema;

export const selectPromoModelResponseSchema = z
    .object({
        isSelected: z.boolean(),
        selectedPromos: z.array(promoWithFormatSchema).optional(),
        translations: translationsSchema.optional(),
        images: imageStoreSchema.optional(),
    })
    .passthrough() as z.ZodType<SelectPromoModelResponse>;

export const selectMultiplePromosModelResponseSchema = z
    .object({
        regular: selectPromoModelResponseSchema,
        persistent: selectPromoModelResponseSchema.optional().default(() => ({ isSelected: false })),
    })
    .strict() as z.ZodType<SelectMultiplePromosModelResponse>;
